import React, { useState } from "react";
import { Box, TextField, Button, Popover, Typography } from "@mui/material";
import {
  CalendarMonth as CalendarIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

interface DateTimeRangePickerProps {
  fromDateTime?: string;
  toDateTime?: string;
  onDateTimeChange: (from: string, to: string) => void;
  onClear: () => void;
  label?: string;
  variant?: "button" | "inline";
}

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  fromDateTime,
  toDateTime,
  onDateTimeChange,
  onClear,
  label = "Select DateTime Range",
  variant = "button",
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tempFromDateTime, setTempFromDateTime] = useState<string>(
    fromDateTime ?? "",
  );
  const [tempToDateTime, setTempToDateTime] = useState<string>(
    toDateTime ?? "",
  );
  const [inlineFrom, setInlineFrom] = useState(fromDateTime ?? "");
  const [inlineTo, setInlineTo] = useState(toDateTime ?? "");

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTempFromDateTime(fromDateTime ?? "");
    setTempToDateTime(toDateTime ?? "");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (tempFromDateTime && tempToDateTime) {
      onDateTimeChange(tempFromDateTime, tempToDateTime);
    }
    handleClose();
  };

  const handleClear = () => {
    setTempFromDateTime("");
    setTempToDateTime("");
    onClear();
    setInlineFrom("");
    setInlineTo("");
    handleClose();
  };

  const handleInlineChange =
    (field: "from" | "to") => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const nextFrom = field === "from" ? value : inlineFrom;
      const nextTo = field === "to" ? value : inlineTo;

      setInlineFrom(nextFrom);
      setInlineTo(nextTo);

      if (nextFrom && nextTo) {
        onDateTimeChange(nextFrom, nextTo);
      } else if (!nextFrom || !nextTo) {
        onClear();
      }
    };

  const handleInlineClear = () => {
    setInlineFrom("");
    setInlineTo("");
    onClear();
  };

  React.useEffect(() => {
    setInlineFrom(fromDateTime ?? "");
  }, [fromDateTime]);

  React.useEffect(() => {
    setInlineTo(toDateTime ?? "");
  }, [toDateTime]);

  const formatDateTime = (dateTimeStr: string): string => {
    if (!dateTimeStr) return "";
    try {
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const getDisplayText = () => {
    if (fromDateTime && toDateTime) {
      const fromFormatted = formatDateTime(fromDateTime);
      const toFormatted = formatDateTime(toDateTime);
      if (fromFormatted && toFormatted) {
        return `${fromFormatted} - ${toFormatted}`;
      }
    }
    return label;
  };

  if (variant === "inline") {
    return (
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
        <TextField
          label="From"
          type="datetime-local"
          value={inlineFrom}
          onChange={handleInlineChange("from")}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="To"
          type="datetime-local"
          value={inlineTo}
          onChange={handleInlineChange("to")}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleInlineClear}
          disabled={!inlineFrom && !inlineTo}
          startIcon={<ClearIcon fontSize="small" />}
        >
          Clear
        </Button>
      </Box>
    );
  }

  const open = Boolean(anchorEl);

  return (
    <Box>
      <Button
        variant="outlined"
        fullWidth
        startIcon={<CalendarIcon />}
        onClick={handleClick}
        sx={(theme) => ({
          height: 56,
          justifyContent: "flex-start",
          textTransform: "none",
          fontSize: "0.85rem",
          color: theme.palette.text.primary,
          borderRadius: 1,
          borderColor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[700]
              : theme.palette.grey[300],
          "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        {getDisplayText()}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 320 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, color: "text.secondary" }}
          >
            {label}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <TextField
              label="From DateTime"
              type="datetime-local"
              value={tempFromDateTime}
              onChange={(e) => setTempFromDateTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <TextField
              label="To DateTime"
              type="datetime-local"
              value={tempToDateTime}
              onChange={(e) => setTempToDateTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button onClick={handleClose} size="small">
              Cancel
            </Button>
            <Button onClick={handleClear} size="small" color="secondary">
              Clear
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              size="small"
              disabled={!tempFromDateTime || !tempToDateTime}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateTimeRangePicker;
